/**
 * Groups tours into the per-BU sections the Tours screen renders.
 *
 * @module Pages/BUs/Tours
 */

import type { Tour } from '../Types/Tours.types';
import { compareTenantSlugs } from '@/Shared/brand/tenantOrder';

/** One BU's section of tours. */
export interface TenantTourGroup {
  tenantSlug: string;
  tours: Tour[];
  /** e.g. "3 tours". */
  countLabel: string;
  /** e.g. "2 ativos". */
  activeLabel: string;
}

/**
 * Groups by tenant slug, tenants ordered by compareTenantSlugs and tours inside each ordered by page slug, so a
 * BU's list does not reshuffle when a tour is bumped or toggled.
 */
export function groupToursByTenant(tours: Tour[]): TenantTourGroup[] {
  const byTenant = new Map<string, Tour[]>();

  for (const tour of tours) {
    const existing = byTenant.get(tour.tenantSlug);
    if (existing) {
      existing.push(tour);
    } else {
      byTenant.set(tour.tenantSlug, [tour]);
    }
  }

  return [...byTenant.entries()]
    .sort(([a], [b]) => compareTenantSlugs(a, b))
    .map(([tenantSlug, tenantTours]) => {
      const ordered = [...tenantTours].sort((a, b) => a.pageSlug.localeCompare(b.pageSlug));
      const activeCount = ordered.filter(tour => tour.isActive).length;

      return {
        tenantSlug,
        tours: ordered,
        countLabel: `${ordered.length} ${ordered.length === 1 ? 'tour' : 'tours'}`,
        activeLabel: `${activeCount} ${activeCount === 1 ? 'ativo' : 'ativos'}`,
      };
    });
}
